import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import sumBy from 'lodash/sumBy';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import ITimeRange from 'types/TimeRange';

const PROGRESS_ENTRY_COUNTS_QUERY = gql`
  query ProgressEntryCounts($ranges: [DashboardRangeInput!]!, $memberId: Uuid, $projectId: Uuid, $costCodeId: Uuid) {
    dashboard {
      progressEntryCounts(ranges: $ranges, memberId: $memberId, projectId: $projectId, costCodeId: $costCodeId) {
        startTime
        endTime
        count
      }
    }
  }
`;

interface IProgressEntryRangeCount {
  startTime: string;
  endTime: string;
  count: number;
}

export default function useProgressDashboardCard(
  timeRange: ITimeRange<DateTime>,
  ranges: Array<ITimeRange<number>>,
  memberId?: string,
  projectId?: string,
  costCodeId?: string,
  enabled: boolean = true
) {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();

  // Bars come from useDashboardGraphDates as UTC epoch seconds; assignedOn is a naive datetime bucketed
  // by its wall clock, so send the boundaries as UTC wall-clock timestamps (no offset shift).
  const rangesKey = JSON.stringify(ranges);
  const serverRanges = useMemo(
    () =>
      ranges.map((range) => ({
        startTime: DateTime.fromSeconds(range.startTime, { zone: 'utc' }).toISO({
          suppressMilliseconds: true,
          includeOffset: false,
        }),
        endTime: DateTime.fromSeconds(range.endTime, { zone: 'utc' }).toISO({
          suppressMilliseconds: true,
          includeOffset: false,
        }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangesKey]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...baseKey, 'progress-entries-dashboard', { rangesKey, memberId, projectId, costCodeId }],
    queryFn: async () => {
      const result = await graphQLClient.request<{ dashboard: { progressEntryCounts: IProgressEntryRangeCount[] } }>({
        document: PROGRESS_ENTRY_COUNTS_QUERY,
        variables: {
          ranges: serverRanges,
          memberId: memberId ?? null,
          projectId: projectId ?? null,
          costCodeId: costCodeId ?? null,
        },
      });
      return result.dashboard.progressEntryCounts;
    },
    enabled,
  });

  const chartData = ranges.map((range, index) => ({ day: range.startTime, value: data?.[index]?.count ?? 0 }));

  return {
    data: chartData,
    total: sumBy(chartData, (datum) => datum.value),
    loading: isLoading,
    error: error ?? null,
    execute: refetch,
  };
}
